// Draws a QR code with the Schick icon in its centre, as the website's download dialog shows it:
// the modules under the icon are left out (error correction H restores them), the icon is the
// blue rounded square with the white mark. One geometry feeds the preview, the SVG and the PNG.

export const QUIET = 4; // white modules around the code, as scanners expect
export const DARK = '#0b0f19';
export const BRAND = '#006cfd';

// The white mark inside the icon, placed as in the app icon (and the website's dialog).
const MARK = { left: 0.221, top: 0.274, width: 0.591, ratio: 502 / 600 };

/** Half the side of the empty centre in modules; the icon fills it but for half a module each side. */
const holeRadius = (n) => Math.ceil(Math.round(n * 0.22) / 2);

/** The code's geometry in module units, quiet zone included. */
export function qrGeometry(rows) {
  const n = rows.length;
  const size = n + 2 * QUIET;
  const c = (n - 1) / 2;
  const r = holeRadius(n);
  const inHole = (x, y) => Math.abs(x - c) <= r && Math.abs(y - c) <= r;

  const modules = [];
  rows.forEach((row, y) => {
    for (let x = 0; x < n; x++) {
      if (row[x] === '1' && !inHole(x, y)) modules.push([x + QUIET, y + QUIET]);
    }
  });

  const side = 2 * r; // the hole is 2r + 1 modules wide
  const logo = { x: QUIET + c - r + 0.5, y: QUIET + c - r + 0.5, side, radius: side * 0.225 };
  const markWidth = side * MARK.width;
  const mark = {
    x: logo.x + side * MARK.left,
    y: logo.y + side * MARK.top,
    width: markWidth,
    height: markWidth * MARK.ratio,
  };
  return { n, size, modules, logo, mark };
}

/** The dark modules as one SVG path. */
export const modulePath = (modules) => modules.map(([x, y]) => `M${x} ${y}h1v1h-1z`).join('');

/** A standalone SVG file; `markHref` should be a data: URL so the file works on its own. */
export function qrSvg(rows, markHref, pixels = 1024) {
  const g = qrGeometry(rows);
  const f = (v) => +v.toFixed(3);
  return [
    `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 ${g.size} ${g.size}" width="${pixels}" height="${pixels}">`,
    `<rect width="${g.size}" height="${g.size}" fill="#ffffff"/>`,
    `<path fill="${DARK}" shape-rendering="crispEdges" d="${modulePath(g.modules)}"/>`,
    `<rect x="${f(g.logo.x)}" y="${f(g.logo.y)}" width="${g.logo.side}" height="${g.logo.side}" rx="${f(g.logo.radius)}" fill="${BRAND}"/>`,
    `<image x="${f(g.mark.x)}" y="${f(g.mark.y)}" width="${f(g.mark.width)}" height="${f(g.mark.height)}" xlink:href="${markHref}"/>`,
    '</svg>',
  ].join('');
}

const roundedRect = (ctx, x, y, w, h, r) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
};

/**
 * A PNG of about `target` pixels: whole pixels per module so the edges stay sharp.
 * `markImage` is a loaded <img> of the white mark.
 */
export function qrPng(rows, markImage, target = 2048) {
  const g = qrGeometry(rows);
  const unit = Math.max(1, Math.floor(target / g.size));
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = g.size * unit;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = DARK;
  g.modules.forEach(([x, y]) => ctx.fillRect(x * unit, y * unit, unit, unit));
  ctx.fillStyle = BRAND;
  roundedRect(ctx, g.logo.x * unit, g.logo.y * unit, g.logo.side * unit, g.logo.side * unit, g.logo.radius * unit);
  ctx.fill();
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(markImage, g.mark.x * unit, g.mark.y * unit, g.mark.width * unit, g.mark.height * unit);
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('PNG konnte nicht erzeugt werden.'))), 'image/png');
  });
}
